import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Drawer, Select } from "@outpave/ds";
import LanguageSelect from "components/common/LanguageSelect";
import { Stack } from "components/layout/reusableLayouts";
import {
  THEME_PRESETS_ENABLED,
  THEME_PRESET_NAMES,
  isThemePresetName,
  setThemePreset,
  useThemePreset,
} from "theme/themePresetStore";

interface PreferencesDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
}

// Proper names, like the language names beside them, so they are not translated.
const presetLabel = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1);

/** Display preferences, kept in this browser rather than on the person's record. */
const PreferencesDrawer: React.FC<PreferencesDrawerProps> = ({
  open,
  handleCloseDrawer,
}) => {
  const { t } = useTranslation();
  const themePreset = useThemePreset();

  return (
    <Drawer
      open={open}
      onClose={handleCloseDrawer}
      title={[t("Profile"), t("Your Preferences")]}
      footer={
        <Button variant="default-soft" fullWidth onClick={handleCloseDrawer}>
          {t("Exit")}
        </Button>
      }
    >
      <Stack gap="var(--space-6)">
        <LanguageSelect label={t("Language")} />
        {THEME_PRESETS_ENABLED && (
          <Select
            label={t("Theme")}
            helperText={t("ThemePresetHelp")}
            clearable={false}
            options={THEME_PRESET_NAMES.map((name) => ({
              id: name,
              label: presetLabel(name),
            }))}
            value={themePreset}
            onChange={(value) => {
              if (isThemePresetName(value)) setThemePreset(value);
            }}
          />
        )}
      </Stack>
    </Drawer>
  );
};

export default PreferencesDrawer;
